/**
 * Vincent P1.8.d (Eric 2026-07-31 SPEC STRICK PROD) — Extraction du hook
 * `useVideoActions` depuis `VideoDetailPage.js`.
 *
 * SCOPE — Ce hook regroupe les actions utilisateur qui modifient la fiche
 * vidéo. Concrètement, en date de l'extraction, cela couvre le **flux
 * d'édition des métadonnées** (titre, description, thème, niveau d'accès,
 * URL Dropbox, catégorie, parcours), soit 9 states + `startEdit` + `saveEdit`.
 *
 * Actions volontairement EXCLUES de ce hook :
 *   - `handleReprocessSpeakers` — reste dans VideoDetailPage : c'est un
 *     wrapper de 5 lignes autour de `useSpeakerReprocess` (déjà un hook).
 *   - `handleRerunWhisper` — vit dans `components/video/WhisperToolbar.js`
 *     et `components/video/VideoPipelineStatus.js` (jamais dans la page).
 *   - `handleDeleteVideo` — vit dans `components/video/VideoDetailModals.js`
 *     (la modale de confirmation contient toute la logique de suppression).
 *
 * Contrat :
 *   - Reçoit `{ video, videoId, api, allPathways, onSaved }`.
 *   - `onSaved` est appelé après un save réussi (typiquement pour
 *     déclencher `loadVideoData()` + `loadCategoriesAndPathways()`).
 *   - Utilise les helpers partagés `saveCategoryIfChanged` /
 *     `savePathwayDiff` / `pathwayIdsForVideo` de `lib/videoEditPersistence.js`
 *     — source unique de vérité (mêmes helpers que FileExplorerPage).
 */
import { useState, useCallback } from "react";
import axios from "axios";
import {
  pathwayIdsForVideo,
  saveCategoryIfChanged,
  savePathwayDiff,
} from "../lib/videoEditPersistence";

export function useVideoActions({ video, videoId, api, allPathways, onSaved }) {
  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editTheme, setEditTheme] = useState("");
  const [editAccessLevel, setEditAccessLevel] = useState(1);
  const [editDropbox, setEditDropbox] = useState("");
  const [editCategoryId, setEditCategoryId] = useState("");
  const [editSelectedPathwayIds, setEditSelectedPathwayIds] = useState(() => new Set());
  const [editSaving, setEditSaving] = useState(false);

  const startEdit = useCallback(() => {
    setEditTitle(video?.title || "");
    setEditDesc(video?.description || "");
    setEditTheme(video?.training_theme || "");
    setEditAccessLevel(video?.required_access_level || 1);
    setEditDropbox(video?.dropbox_url || "");
    // Vincent (2026-02) bugs #524/#525 — init depuis les données réelles
    // via helpers partagés (lib/videoEditPersistence.js).
    setEditCategoryId(video?.category_id || "");
    setEditSelectedPathwayIds(pathwayIdsForVideo(allPathways, videoId));
    setEditMode(true);
  }, [video, allPathways, videoId]);

  const saveEdit = useCallback(async () => {
    setEditSaving(true);
    try {
      // 1) Métadonnées de base (titre, description, dropbox). training_theme et
      // required_access_level sont conservés côté DB pour rétrocompat, mais
      // ne sont plus modifiables via cette UI (Vincent #524/#525).
      await axios.put(`${api}/api/videos/${videoId}`, {
        title: editTitle,
        description: editDesc,
        training_theme: editTheme,
        required_access_level: editAccessLevel,
        dropbox_url: editDropbox.trim() || null,
      }, { withCredentials: true });
      // 2) Vincent (2026-02) — Catégorie + Parcours via helpers partagés
      // (même logique que FileExplorerPage → source unique de vérité).
      await saveCategoryIfChanged(api, videoId, video?.category_id || "", editCategoryId || "");
      await savePathwayDiff(
        api, videoId, allPathways,
        pathwayIdsForVideo(allPathways, videoId),
        editSelectedPathwayIds || new Set(),
      );
      setEditMode(false);
      if (typeof onSaved === "function") onSaved();
    } catch (e) {
      console.error(e);
    }
    setEditSaving(false);
  }, [
    api, videoId, video, allPathways,
    editTitle, editDesc, editTheme, editAccessLevel, editDropbox,
    editCategoryId, editSelectedPathwayIds, onSaved,
  ]);

  return {
    editMode, setEditMode,
    editTitle, setEditTitle,
    editDesc, setEditDesc,
    editTheme, setEditTheme,
    editAccessLevel, setEditAccessLevel,
    editDropbox, setEditDropbox,
    editCategoryId, setEditCategoryId,
    editSelectedPathwayIds, setEditSelectedPathwayIds,
    editSaving,
    startEdit,
    saveEdit,
  };
}
